import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Heart, MapPin, Fuel, Gauge, Calendar, Phone, User, Eye, Star,
  ShoppingCart, ExternalLink, CheckCircle, Settings, X, ClipboardList,
  ChevronDown, ChevronUp, Shield, FileCheck, AlertTriangle, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { FaWhatsapp } from "react-icons/fa";
import tinycolor from "tinycolor2";
import verifiedBadge from "../assets/verified.png";
import ChatButton from '@/components/ChatButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifiedBadge = ({ className = "h-6", style = {} }) => (
  <img src={verifiedBadge} alt="Verified" className={`object-contain drop-shadow-sm ${className}`} style={{ maxWidth: '90px', ...style }} />
);

// ==================== VERIFIED INSURANCE & PUC SECTION ====================
const VerifiedInsurancePUCSection = ({ inspection }) => {
  if (!inspection) return null;
  const ipv = inspection.checklist_data?.insurance_puc_verification;
  if (!ipv) return null;
  const adminData = inspection.admin_verified_data || {};
  const insuranceCompany = adminData.insurance_company || ipv.verified_insurance_company;
  const insuranceExpiry  = adminData.insurance_expiry  || ipv.verified_insurance_expiry;
  const pucExpiry        = adminData.puc_expiry        || ipv.verified_puc_expiry;
  const insuranceValid   = ipv.insurance_valid;
  const pucValid         = ipv.puc_valid;
  if (!insuranceCompany && !insuranceExpiry && !pucExpiry) return null;
  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  return (
    <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-5 border border-blue-100">
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-1.5">
          <VerifiedBadge className="h-6" />
          <span className="text-sm font-bold text-blue-800">Partner-Verified Documents</span>
        </div>
        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">Physically Inspected</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className={`rounded-xl p-4 border-2 ${insuranceValid ? 'bg-white border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Shield className={`h-4 w-4 ${insuranceValid ? 'text-blue-600' : 'text-gray-400'}`} />
            <span className={`text-xs font-bold ${insuranceValid ? 'text-blue-700' : 'text-gray-500'}`}>{insuranceValid ? 'Insurance Valid' : 'No Insurance'}</span>
          </div>
          {insuranceValid && (
            <div className="space-y-1">
              {insuranceCompany && <div className="flex items-center gap-1.5"><Building2 className="h-3 w-3 text-gray-400" /><span className="text-xs font-semibold text-gray-700">{insuranceCompany}</span></div>}
              {insuranceExpiry  && <div className="flex items-center gap-1.5"><Calendar className="h-3 w-3 text-gray-400" /><span className="text-xs text-gray-600">Expires: {formatDate(insuranceExpiry)}</span></div>}
              {ipv.insurance_company_mismatch || ipv.insurance_expiry_mismatch
                ? <div className="mt-2 flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded-lg"><AlertTriangle className="h-3 w-3" /> Corrected by partner</div>
                : <div className="mt-2 flex items-center gap-1 text-[10px] text-green-600"><CheckCircle className="h-3 w-3" /> Matches seller's data</div>}
            </div>
          )}
        </div>
        <div className={`rounded-xl p-4 border-2 ${pucValid ? 'bg-white border-green-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <FileCheck className={`h-4 w-4 ${pucValid ? 'text-green-600' : 'text-gray-400'}`} />
            <span className={`text-xs font-bold ${pucValid ? 'text-green-700' : 'text-gray-500'}`}>{pucValid ? 'PUC Valid' : 'No PUC'}</span>
          </div>
          {pucValid && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-gray-500">Pollution Under Control</span>
              {pucExpiry && <div className="flex items-center gap-1.5"><Calendar className="h-3 w-3 text-gray-400" /><span className="text-xs text-gray-600">Expires: {formatDate(pucExpiry)}</span></div>}
              {ipv.puc_expiry_mismatch
                ? <div className="mt-2 flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded-lg"><AlertTriangle className="h-3 w-3" /> Corrected by partner</div>
                : <div className="mt-2 flex items-center gap-1 text-[10px] text-green-600"><CheckCircle className="h-3 w-3" /> Matches seller's data</div>}
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-3 text-center">Data verified by X7 Autoz certified partner during physical inspection</p>
    </div>
  );
};

// ==================== INSPECTION FORM MODAL ====================
const InspectionFormModal = ({ vehicleId, vehicleType, onClose }) => {
  const [inspection, setInspection]       = useState(null);
  const [verificationForm, setVerificationForm] = useState(null);
  const [loading, setLoading]             = useState(true);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try { const r = await axios.get(`${API}/verification-form/${vehicleType}`); setVerificationForm(r.data); } catch {}
      try { const r = await axios.get(`${API}/vehicles/${vehicleId}/inspection`);  setInspection(r.data.inspection); } catch {}
      setLoading(false);
    };
    fetchData();
  }, [vehicleId, vehicleType]);

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));

  const getChecklistSections = () => {
    const sections = {
      car: [
        { section: 'Exterior',   items: ['Body condition (scratches/dents)', 'Paint quality & color match', 'Glass & windshield condition', 'Headlights & taillights working', 'Wipers functional', 'Tyres condition & tread depth'] },
        { section: 'Interior',   items: ['Seats & upholstery condition', 'Dashboard & controls working', 'AC functioning', 'Infotainment/audio system', 'All windows & locks working', 'Odometer reading verified'] },
        { section: 'Mechanical', items: ['Engine noise & performance', 'Brakes functioning', 'Gear shifting smooth', 'Suspension check', 'Steering alignment', 'Battery condition'] },
        { section: 'Documents',  items: ['RC book verified', 'Insurance valid', 'PUC certificate valid', 'Service history available'] },
      ],
      bike: [
        { section: 'Exterior',   items: ['Frame condition (no bends/cracks)', 'Paint quality', 'Headlight & taillight working', 'Mirrors intact', 'Tyres condition'] },
        { section: 'Mechanical', items: ['Engine noise', 'Brakes (front & rear)', 'Chain & sprocket condition', 'Clutch & throttle response', 'Suspension working'] },
        { section: 'Electrical', items: ['Battery condition', 'All indicators working', 'Horn working', 'Speedometer working'] },
        { section: 'Documents',  items: ['RC book verified', 'Insurance valid', 'PUC valid'] },
      ],
      ev: [
        { section: 'Battery & Charging', items: ['Battery health indicator', 'Charging port condition (AC/DC)', 'Onboard charger working', 'Estimated range verified'] },
        { section: 'Exterior',   items: ['Body condition', 'Paint quality', 'Glass condition', 'Headlights & taillights'] },
        { section: 'Interior',   items: ['Seats & upholstery', 'Touchscreen/infotainment', 'AC & climate control', 'Sunroof (if any)'] },
        { section: 'Mechanical', items: ['Motor noise', 'Brakes (regen + hydraulic)', 'Suspension', 'Steering response'] },
        { section: 'Documents',  items: ['RC book', 'Insurance', 'Warranty remaining', 'Service history'] },
      ],
      commercial: [
        { section: 'Exterior & Structure', items: ['Cabin condition', 'Body/cargo area condition', 'Frame integrity', 'Tyres (all axles)', 'Lights & indicators'] },
        { section: 'Mechanical', items: ['Engine condition & noise', 'Brakes (including air brakes)', 'Gearbox condition', 'Clutch', 'Suspension & chassis'] },
        { section: 'Electrical', items: ['Battery & alternator', 'Dashboard instruments', 'Horn & wipers'] },
        { section: 'Documents',  items: ['RC book', 'Fitness certificate', 'Insurance', 'Permit (if applicable)', 'PUC'] },
      ],
    };
    return sections[vehicleType] || sections.car;
  };

  const getStatusBadge = (val) => {
    if (val === 'ok')    return <span className="text-xs font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">✓ OK</span>;
    if (val === 'issue') return <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">⚠ Issue</span>;
    if (val === 'na')    return <span className="text-xs font-bold text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full">N/A</span>;
    return <span className="text-xs text-gray-400">—</span>;
  };

  const checklistData = inspection?.checklist_data || {};
  const remarks       = checklistData.remarks || {};
  const ipv           = checklistData.insurance_puc_verification;
  const adminData     = inspection?.admin_verified_data || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <VerifiedBadge className="h-8" />
              <div>
                <h2 className="text-lg font-bold text-white">Inspection Report</h2>
                <p className="text-blue-200 text-xs mt-0.5">Verified by X7 Autoz Partner</p>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition">
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Loading inspection report...</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">This vehicle has been physically inspected and verified by a certified X7 Autoz partner.</p>
              </div>

              {ipv && (
                <div className="border border-blue-200 rounded-xl overflow-hidden">
                  <button className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 transition" onClick={() => toggleSection('insurance_puc')}>
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-bold text-blue-800 uppercase tracking-wide">Insurance & PUC Verification</span>
                    </div>
                    {expandedSections['insurance_puc'] ? <ChevronUp className="h-4 w-4 text-blue-500" /> : <ChevronDown className="h-4 w-4 text-blue-500" />}
                  </button>
                  {expandedSections['insurance_puc'] && (
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                          <div className="flex items-center gap-2 mb-2"><Shield className="h-4 w-4 text-blue-500" /><span className="text-xs font-bold text-blue-700">Insurance</span></div>
                          <p className="text-xs text-gray-600">Company:</p>
                          <p className="text-sm font-bold text-gray-800">{adminData.insurance_company || ipv.verified_insurance_company || '—'}</p>
                          <p className="text-xs text-gray-600 mt-1">Expiry:</p>
                          <p className="text-sm font-semibold text-gray-800">
                            {(adminData.insurance_expiry || ipv.verified_insurance_expiry) ? new Date(adminData.insurance_expiry || ipv.verified_insurance_expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                          </p>
                          {(ipv.insurance_company_mismatch || ipv.insurance_expiry_mismatch) && <p className="text-[10px] text-amber-600 mt-1.5 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Corrected from seller's data</p>}
                        </div>
                        <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                          <div className="flex items-center gap-2 mb-2"><FileCheck className="h-4 w-4 text-green-500" /><span className="text-xs font-bold text-green-700">PUC</span></div>
                          <p className="text-xs text-gray-600">Status:</p>
                          <p className={`text-sm font-bold ${ipv.puc_valid ? 'text-green-700' : 'text-red-600'}`}>{ipv.puc_valid ? '✅ Valid' : '❌ Not Valid'}</p>
                          <p className="text-xs text-gray-600 mt-1">Expiry:</p>
                          <p className="text-sm font-semibold text-gray-800">
                            {(adminData.puc_expiry || ipv.verified_puc_expiry) ? new Date(adminData.puc_expiry || ipv.verified_puc_expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                          </p>
                          {ipv.puc_expiry_mismatch && <p className="text-[10px] text-amber-600 mt-1.5 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Corrected from seller's data</p>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {verificationForm?.questions?.length > 0 && (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <button className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition" onClick={() => toggleSection('questions')}>
                    <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">Verification Questions</span>
                    {expandedSections['questions'] ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                  </button>
                  {expandedSections['questions'] && (
                    <div className="p-4 space-y-3">
                      {verificationForm.questions.map((q, idx) => {
                        const answer = checklistData.form_answers?.[q.id || idx];
                        return (
                          <div key={q.id || idx} className="flex items-start justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-700 flex-1">{q.question}</p>
                            <div className="flex-shrink-0">
                              {answer
                                ? (q.type === 'km_run'
                                    ? <span className="text-sm font-bold text-gray-800 bg-white border border-gray-200 px-3 py-1 rounded-lg">{adminData.km_run || answer} km</span>
                                    : <span className={`text-xs font-bold px-3 py-1 rounded-full ${answer === 'Yes' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>{answer}</span>)
                                : <span className="text-xs text-gray-400">Not answered</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {getChecklistSections().map((section) => (
                <div key={section.section} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition" onClick={() => toggleSection(section.section)}>
                    <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">{section.section}</span>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const ok     = section.items.filter(i => checklistData[i] === 'ok').length;
                        const issues = section.items.filter(i => checklistData[i] === 'issue').length;
                        return (
                          <div className="flex gap-1.5 text-[10px] font-semibold">
                            {ok     > 0 && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{ok} OK</span>}
                            {issues > 0 && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{issues} Issue{issues > 1 ? 's' : ''}</span>}
                          </div>
                        );
                      })()}
                      {expandedSections[section.section] ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                    </div>
                  </button>
                  {expandedSections[section.section] && (
                    <div className="divide-y divide-gray-100">
                      {section.items.map(item => (
                        <div key={item} className="flex items-start justify-between gap-3 px-4 py-3">
                          <div className="flex-1">
                            <p className="text-sm text-gray-700">{item}</p>
                            {checklistData[item] === 'issue' && remarks[item] && <p className="text-xs text-red-600 mt-1 italic">"{remarks[item]}"</p>}
                          </div>
                          <div className="flex-shrink-0">{getStatusBadge(checklistData[item])}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {checklistData.overall_notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-yellow-800 uppercase tracking-wide mb-2">Partner Notes</p>
                  <p className="text-sm text-yellow-900">{checklistData.overall_notes}</p>
                </div>
              )}

              {!inspection && !verificationForm && (
                <div className="text-center py-8 text-gray-400">
                  <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Inspection report not available</p>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-xs text-gray-400 text-center">This report is for reference only. X7 Autoz does not guarantee accuracy of all inspection points.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== VEHICLE DETAIL ====================
const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [selectedCity, setSelectedCity]   = useState('Belgaum');
  const [cities, setCities]               = useState([]);
  const [vehicle, setVehicle]             = useState(null);
  const [relatedVehicles, setRelatedVehicles] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [loading, setLoading]             = useState(true);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [purchaseMessage, setPurchaseMessage]       = useState('');
  const [sendingRequest, setSendingRequest]         = useState(false);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [isOpen, setIsOpen]               = useState(false);
  const [wishlistedIds, setWishlistedIds] = useState(new Set());
  const [showInspectionForm, setShowInspectionForm] = useState(false);
  const [inspection, setInspection]       = useState(null);
  const isWishlisted = vehicle && wishlistedIds.has(vehicle.id);

  useEffect(() => { fetchCities(); fetchVehicle(); }, [id]);

  const fetchCities = async () => { try { const r = await axios.get(`${API}/cities`); setCities(r.data.cities); } catch {} };

  const fetchVehicle = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vehicles/${id}`);
      setVehicle(res.data.vehicle);
      setRelatedVehicles(res.data.related_vehicles);
      if (res.data.vehicle?.seller_id) fetchSellerProfile(res.data.vehicle.seller_id);
      if (res.data.vehicle?.verified_by_x7autoz) {
        try { const inspRes = await axios.get(`${API}/vehicles/${id}/inspection`); setInspection(inspRes.data.inspection); } catch {}
      }
    } catch { toast.error('Vehicle not found'); navigate('/browse'); }
    finally { setLoading(false); }
  };

  const fetchWishlist = async () => {
    if (!user) return;
    try { const r = await axios.get(`${API}/wishlist`, { headers: { Authorization: `Bearer ${token}` } }); setWishlistedIds(new Set(r.data.vehicles.map(v => v.id))); } catch {}
  };

  const handleAddToWishlist = async (vehicleId) => {
    if (!user) { navigate('/login'); return; }
    try {
      if (wishlistedIds.has(vehicleId)) {
        await axios.delete(`${API}/wishlist/${vehicleId}`, { headers: { Authorization: `Bearer ${token}` } });
        setWishlistedIds(prev => { const s = new Set(prev); s.delete(vehicleId); return s; });
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist?vehicle_id=${vehicleId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setWishlistedIds(prev => new Set([...prev, vehicleId]));
        toast.success('Added to wishlist!');
      }
    } catch { toast.error('Failed to update wishlist'); }
  };

  useEffect(() => { if (user && vehicle) fetchWishlist(); }, [user, vehicle]);

  const handleContact  = () => { if (!user) { navigate('/login'); return; } window.open(`tel:${vehicle.contact_phone}`); };
  const handleWhatsApp = () => {
    if (!user) { navigate('/login'); return; }
    const msg = encodeURIComponent(`Hi, I'm interested in your ${vehicle.brand_name} ${vehicle.model_name} (${vehicle.registration_no})`);
    window.open(`https://wa.me/${vehicle.contact_phone}?text=${msg}`, '_blank');
  };
  const fetchSellerProfile = async (sellerId) => { try { const r = await axios.get(`${API}/users/${sellerId}/profile`); setSellerProfile(r.data.profile); } catch {} };

  const sendPurchaseRequest = async () => {
    if (!user) { toast.error('Please login'); navigate('/login'); return; }
    setSendingRequest(true);
    try {
      await axios.post(`${API}/purchase-requests`, { vehicle_id: vehicle.id, seller_id: vehicle.seller_id, message: purchaseMessage }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Purchase request sent!');
      setShowPurchaseDialog(false); setPurchaseMessage('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send request'); }
    finally { setSendingRequest(false); }
  };

  const getVehicleColor = (color) => { const p = tinycolor(color); return p.isValid() ? p.toHexString() : "linear-gradient(135deg,#ddd,#888)"; };

  if (loading) return (
    <div className="min-h-screen bg-secondary-50">
      <Header selectedCity={selectedCity} setSelectedCity={setSelectedCity} cities={cities} />
      <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" /></div>
    </div>
  );
  if (!vehicle) return null;

  // True only when the logged-in user is the seller — hides the chat button
  const viewerIsSeller = user && vehicle.seller_id === user.id;

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header selectedCity={selectedCity} setSelectedCity={setSelectedCity} cities={cities} />

      <div className="px-4 md:px-8 lg:px-12 py-8">
        <Button variant="ghost" onClick={() => navigate('/browse')} className="mb-6">← Back to Browse</Button>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">

          {/* ── LEFT COLUMN: Image Gallery ── */}
          <div className="space-y-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="relative h-96 rounded-lg overflow-hidden bg-white">
              <img
                src={vehicle.images[selectedImage] || 'https://images.unsplash.com/photo-1676288176820-a5a954d81e6e?w=800'}
                alt={`${vehicle.brand_name} ${vehicle.model_name}`}
                className="w-full h-full object-contain cursor-pointer"
                style={{ imageOrientation: 'from-image' }}
                onClick={() => setIsOpen(true)}
              />
            </motion.div>

            {vehicle.images.length > 1 && (
              <div className="grid grid-cols-6 gap-2">
                {vehicle.images.map((img, idx) => (
                  <div key={idx} onClick={() => setSelectedImage(idx)} className={`h-20 rounded-lg overflow-hidden cursor-pointer border-2 ${selectedImage === idx ? 'border-primary-500' : 'border-transparent'}`}>
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}

            {/*
              Chat button — visible to EVERYONE except the seller themselves.
              Guests see it too; clicking redirects them to /login (handled inside ChatButton).
              ChatButton shows "Chat with <Seller Name>" using vehicle.seller_name.
            */}
            {!viewerIsSeller && (
              <ChatButton vehicle={vehicle} />
            )}
          </div>

          {/* ── RIGHT COLUMN: Vehicle Info ── */}
          <div className="space-y-6">
            <div>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <h1 className="text-4xl font-bold text-secondary-800">{vehicle.brand_name} {vehicle.model_name}</h1>
                    {vehicle.verified_by_x7autoz && <VerifiedBadge className="h-8 flex-shrink-0" />}
                  </div>
                  <p className="text-lg text-secondary-600">{vehicle.variant_name}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); handleAddToWishlist(vehicle.id); }}>
                  <Heart className={`h-5 w-5 ${isWishlisted ? "text-red-500 fill-red-500" : "text-gray-400 hover:text-red-400"}`} />
                </button>
              </div>
              <div className="flex items-center gap-4 text-sm text-secondary-600">
                <div className="flex items-center gap-1"><Eye className="h-4 w-4" />{vehicle.views_count} views</div>
                <Badge className={`${vehicle.fuel_type?.toLowerCase() === "electric" ? "bg-green-500 text-white hover:bg-green-500" : vehicle.fuel_type?.toLowerCase() === "petrol" ? "bg-blue-100 text-blue-700 hover:bg-blue-100" : vehicle.fuel_type?.toLowerCase() === "diesel" ? "bg-orange-200 text-orange-700 hover:bg-orange-200" : "bg-gray-100 text-gray-700"}`}>{vehicle.registration_no}</Badge>
              </div>
              <br />
              {vehicle.is_choice_number && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-50 border border-orange-300 text-orange-700 text-xs font-bold rounded-full">
                  <Star className="h-3 w-3 fill-orange-500 text-orange-500" />Choice Number
                </span>
              )}
            </div>

            <div className="text-4xl font-bold text-amber-900">{vehicle.price}</div>

            <div className="bg-gray-50 rounded-3xl overflow-hidden border border-gray-200">
              <div className="grid grid-cols-2">
                <div className="flex items-center gap-3 p-4 border-b border-r border-gray-200"><Fuel className="h-5 w-5 text-secondary-600" /><div className='space-y-2'><p className="text-sm text-secondary-600">FUEL TYPE</p><p className="font-semibold text-secondary-800">{vehicle.fuel_type}</p></div></div>
                <div className="flex items-center gap-3 p-4 border-b border-gray-200"><Gauge className="h-5 w-5 text-secondary-600" /><div className='space-y-2'><p className="text-sm text-secondary-600">KM DRIVEN</p><p className="font-semibold text-secondary-800">{vehicle.km_run_range}</p></div></div>
                <div className="flex items-center gap-3 p-4 border-b border-r border-gray-200"><Calendar className="h-5 w-5 text-secondary-600" /><div className='space-y-2'><p className="text-sm text-secondary-600">YEAR</p><p className="font-semibold text-secondary-800">{vehicle.manufacturing_year}</p></div></div>
                <div className="flex items-center gap-3 p-4 border-b border-gray-200"><User className="h-5 w-5 text-secondary-600" /><div className='space-y-2'><p className="text-sm text-secondary-600">OWNERSHIP</p><p className="font-semibold text-secondary-800">{vehicle.ownership} Owner</p></div></div>
                <div className="flex items-center gap-3 p-4 border-b border-r border-gray-200"><MapPin className="h-5 w-5 text-secondary-600" /><div className='space-y-2'><p className="text-sm text-secondary-600">LOCATION</p><p className="font-semibold text-secondary-800">{vehicle.city}</p></div></div>
                <div className="flex items-center gap-3 p-4"><Settings className="h-5 w-5 text-secondary-600" /><div className='space-y-2'><p className="text-sm text-secondary-600">TRANSMISSION</p><p className="font-semibold text-secondary-800">{vehicle.transmission}</p></div></div>
              </div>
            </div>

            {vehicle.verified_by_x7autoz && (
              <button onClick={() => setShowInspectionForm(true)} className="w-full flex items-center justify-between px-5 py-3.5 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-400 rounded-xl transition-all group">
                <div className="flex items-center gap-3">
                  <VerifiedBadge className="h-7 flex-shrink-0" />
                  <div className="text-left">
                    <p className="text-sm font-bold text-blue-800">View Inspection Report</p>
                    <p className="text-xs text-blue-600">Physical verification by X7 Autoz partner</p>
                  </div>
                </div>
                <ClipboardList className="h-5 w-5 text-blue-500 group-hover:text-blue-700 transition-colors" />
              </button>
            )}

            {vehicle.verified_by_x7autoz && inspection && <VerifiedInsurancePUCSection inspection={inspection} />}

            {!vehicle.verified_by_x7autoz && ((vehicle.has_insurance !== null && vehicle.has_insurance !== undefined) || (vehicle.has_puc !== null && vehicle.has_puc !== undefined)) && (
              <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Insurance & PUC</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className={`rounded-xl p-3 border-2 ${vehicle.has_insurance === true ? 'bg-blue-50 border-blue-200' : 'bg-gray-100 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${vehicle.has_insurance === true ? 'bg-blue-500' : 'bg-gray-400'}`}>
                        {vehicle.has_insurance === true ? <svg viewBox="0 0 24 24" className="w-3 h-3 fill-none stroke-white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg> : <svg viewBox="0 0 24 24" className="w-3 h-3 fill-none stroke-white" strokeWidth="3" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>}
                      </div>
                      <p className={`text-xs font-bold ${vehicle.has_insurance === true ? 'text-blue-700' : 'text-gray-500'}`}>{vehicle.has_insurance === true ? 'Insured' : 'No Insurance'}</p>
                    </div>
                    {vehicle.has_insurance === true && vehicle.insurance_company && <p className="text-xs text-blue-600 font-medium truncate pl-7">{vehicle.insurance_company}</p>}
                    {vehicle.has_insurance === true && vehicle.insurance_expiry  && <p className="text-xs text-blue-500 pl-7">Exp: {new Date(vehicle.insurance_expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
                  </div>
                  <div className={`rounded-xl p-3 border-2 ${vehicle.has_puc === true ? 'bg-green-50 border-green-200' : 'bg-gray-100 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${vehicle.has_puc === true ? 'bg-green-500' : 'bg-gray-400'}`}>
                        {vehicle.has_puc === true ? <svg viewBox="0 0 24 24" className="w-3 h-3 fill-none stroke-white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg> : <svg viewBox="0 0 24 24" className="w-3 h-3 fill-none stroke-white" strokeWidth="3" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>}
                      </div>
                      <p className={`text-xs font-bold ${vehicle.has_puc === true ? 'text-green-700' : 'text-gray-500'}`}>{vehicle.has_puc === true ? 'PUC Valid' : 'No PUC'}</p>
                    </div>
                    {vehicle.has_puc === true && vehicle.puc_expiry && <p className="text-xs text-green-500 pl-7">Exp: {new Date(vehicle.puc_expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">As declared by seller — not yet verified</p>
              </div>
            )}

            {vehicle.color && (
              <Card className="mt-4"><CardContent className="p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full border-2 border-gray-200" style={{ background: getVehicleColor(vehicle.color) }} />
                <div><p className="text-sm text-secondary-600">Color</p><p className="font-semibold text-secondary-800">{vehicle.color}</p></div>
              </CardContent></Card>
            )}

            {vehicle.features && vehicle.features.length > 0 && (
              <div className="bg-white rounded-lg px-6 py-5">
                <h3 className="text-xs font-semibold tracking-widest text-gray-500 uppercase mb-3">Key Features</h3>
                <div className="flex flex-wrap gap-2">
                  {vehicle.features.map((feature, idx) => <span key={idx} className="bg-gray-200 text-gray-800 px-4 py-1.5 rounded-sm text-sm font-medium">{feature}</span>)}
                </div>
              </div>
            )}

            <Card className="bg-gray-50 border-none shadow-none"><CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 text-white font-semibold">{vehicle.seller_name?.charAt(0)?.toUpperCase()}</div>
                  <div>
                    <p className="text-xs tracking-widest uppercase text-gray-500 font-semibold">Seller Information</p>
                    <p className="font-semibold text-gray-900 text-base mt-1">{vehicle.seller_name}</p>
                    {vehicle.seller_role === "agent" && <Badge className="bg-purple-100 text-purple-700 mt-1">Verified Agent</Badge>}
                    {sellerProfile && sellerProfile.average_rating > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex items-center">{[1,2,3,4,5].map(star => <Star key={star} className={`h-4 w-4 ${star <= sellerProfile.average_rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}`} />)}</div>
                        <span className="text-sm text-gray-600">{sellerProfile.average_rating} ({sellerProfile.total_ratings} reviews)</span>
                      </div>
                    )}
                  </div>
                </div>
                {vehicle.seller_role === "agent" && <Button variant="outline" size="sm" onClick={() => navigate(`/seller/${vehicle.seller_id}`)}><ExternalLink className="h-4 w-4 mr-1" />View Profile</Button>}
              </div>
            </CardContent></Card>

            <div className="flex gap-4 pt-4">
              <Button size="lg" variant="outline" onClick={handleContact} className="flex-1"><Phone className="mr-2 h-5 w-5" />Call Seller</Button>
              <Button size="lg" onClick={handleWhatsApp} className="flex-1 bg-green-500 hover:bg-green-600"><FaWhatsapp className="mr-2 h-5 w-5" />WhatsApp</Button>
            </div>
          </div>
        </div>

        {relatedVehicles.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-secondary-800 mb-6">Related Vehicles</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              {relatedVehicles.map((rv) => (
                <Card key={rv.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/vehicles/${rv.id}`)}>
                  <div className="h-40 overflow-hidden rounded-t-lg relative">
                    <img src={rv.images[0] || 'https://images.unsplash.com/photo-1676288176820-a5a954d81e6e?w=400'} alt={`${rv.brand_name} ${rv.model_name}`} className="w-full h-full object-cover" />
                    {rv.verified_by_x7autoz && <div className="absolute top-2 left-2"><VerifiedBadge className="h-5" /></div>}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-secondary-800 mb-1">{rv.brand_name} {rv.model_name}</h3>
                    <p className="text-sm text-secondary-600 mb-2">{rv.variant_name}</p>
                    <p className="text-lg font-bold text-primary-500">{rv.price}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />

      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-primary-500" />Send Purchase Request</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg"><p className="font-medium text-gray-800">{vehicle?.brand_name} {vehicle?.model_name}</p><p className="text-primary-600 font-bold">{vehicle?.price}</p></div>
            <div><Label>Message to Seller (Optional)</Label><Textarea value={purchaseMessage} onChange={(e) => setPurchaseMessage(e.target.value)} placeholder="Hi, I'm interested in buying this vehicle. Is it still available?" className="mt-1" /></div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowPurchaseDialog(false)}>Cancel</Button>
              <Button className="flex-1 bg-primary-500 text-black hover:bg-primary-600" onClick={sendPurchaseRequest} disabled={sendingRequest}>
                {sendingRequest ? <span className="flex items-center"><span className="animate-spin mr-2">⏳</span>Sending...</span> : <><CheckCircle className="h-4 w-4 text-black mr-2" />Send Request</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {isOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setIsOpen(false)}>
          <div className="relative bg-white rounded-lg overflow-hidden max-w-4xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <button onClick={() => setIsOpen(false)} className="absolute top-3 right-3 bg-black text-white px-3 py-1 rounded">✕</button>
            <img src={vehicle.images[selectedImage]} alt="Preview" className="w-full max-h-[80vh] object-contain" />
          </div>
        </div>
      )}

      {showInspectionForm && vehicle.verified_by_x7autoz && (
        <InspectionFormModal vehicleId={vehicle.id} vehicleType={vehicle.vehicle_type} onClose={() => setShowInspectionForm(false)} />
      )}
    </div>
  );
};

export default VehicleDetail;